import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { debounce } from 'lodash';

const TIMEOUT_THRESHOLDS: Record<number, number> = {
  25: 120000, // 2 phút
  50: 240000, // 4 phút
  75: 360000, // 6 phút
  100: 480000, // 8 phút
};

type TriggerFunction = (scrollPercent: number, durationMs: number) => void;

const useScrollTimeTracking = (trigger: TriggerFunction) => {
  // 1. Theo dõi trạng thái
  const scrollPercent = useRef(0);
  const startTime = useRef(Date.now()); // Thời điểm trang bắt đầu load

  // Map để theo dõi các ngưỡng đã đạt và thời gian đạt (Tránh gửi lại event)
  const trackedThresholds = useRef<Record<number, boolean>>({});

  const [pageHeight, setPageHeight] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);

  const thresholds = useMemo(() => [25, 50, 75, 100], []);

  // 2. Hàm tính toán cuộn và kiểm tra timeout
  const handleScroll = useCallback(() => {
    // Tính toán phần trăm cuộn
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const footer = document.querySelector('#footer-wrapper')?.clientHeight || 0;
    const totalScrollable = pageHeight - viewportHeight - footer;

    if (totalScrollable <= 0) return;
    const currentPercent = Math.round((scrollTop / totalScrollable) * 100);

    // Thời gian hiện tại từ lúc load trang
    const durationMs = Date.now() - startTime.current;

    // Kiểm tra và kích hoạt sự kiện
    thresholds.some((threshold) => {
      // 1. Đã vượt qua ngưỡng
      // 2. Chưa từng theo dõi ngưỡng này trước đó
      // 3. Thời gian đạt được (durationMs) KHÔNG vượt quá giới hạn timeout
      if (currentPercent >= threshold && scrollPercent.current < threshold && !trackedThresholds.current[threshold]) {
        const timeoutLimit = TIMEOUT_THRESHOLDS[threshold];

        if (durationMs <= timeoutLimit) {
          // Cập nhật và KÍCH HOẠT EVENT
          scrollPercent.current = threshold;
          trackedThresholds.current[threshold] = true; // Đánh dấu đã theo dõi

          if (trigger) {
            trigger(threshold, durationMs);
          }
        } else {
          // Vượt quá giới hạn timeout, đánh dấu để không kiểm tra lại
          trackedThresholds.current[threshold] = true;
        }

        return true; // Dừng vòng lặp ngay khi tìm thấy ngưỡng mới
      }
      return false;
    });
  }, [pageHeight, viewportHeight, thresholds, trigger]);

  // 3. Debounce và Listeners
  const debouncedHandleScroll = useMemo(() => debounce(handleScroll, 20), [handleScroll]);

  useEffect(() => {
    // Logic tính toán lại chiều cao trang và viewport
    const updatePageHeight = () => {
      const height = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight,
        document.body.clientHeight,
        document.documentElement.clientHeight,
      );
      setPageHeight(height);
      setViewportHeight(window.innerHeight);
    };

    // Thiết lập thời gian bắt đầu
    startTime.current = Date.now();
    updatePageHeight();

    window.addEventListener('resize', updatePageHeight);
    window.addEventListener('scroll', debouncedHandleScroll);

    return () => {
      window.removeEventListener('resize', updatePageHeight);
      window.removeEventListener('scroll', debouncedHandleScroll);
    };
  }, [debouncedHandleScroll]);
};

export default useScrollTimeTracking;
